'use client';

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import type { DateSelectArg, EventClickArg, DatesSetArg } from '@fullcalendar/core';
import { DiscoverySuspenseFallback } from '@/platform/layout';
import { SitePageLayout } from '@/platform/layout/SitePageLayout';
import { SiteShell } from '@/platform/layout/SiteShell';
import { useAuth } from '@/platform/auth/useAuth';
import { fetchCalendarEvents, fetchCalendarMeta, fetchMyCalendarEventRequests } from './api';
import { CalendarEventModal } from './CalendarEventModal';
import { CalendarEventRequestsPanel } from './CalendarEventRequestsPanel';
import { apiEventToFullCalendar, draftFromEvent, toDateInput, toTimeInput, upcomingEventsRange } from './eventUtils';
import type { CalendarEvent, CalendarEventRequest, CalendarMeta } from './types';
import './calendar.css';

type ViewName = 'dayGridMonth' | 'timeGridWeek' | 'timeGridDay' | 'listWeek';

const VIEW_TABS: { id: ViewName; label: string }[] = [
  { id: 'dayGridMonth', label: 'Month' },
  { id: 'timeGridWeek', label: 'Week' },
  { id: 'timeGridDay', label: 'Day' },
  { id: 'listWeek', label: 'Schedule' },
];

type CalendarPageProps = {
  fallbackTitle?: string;
  description?: string;
  /** Product tour: fixture events, no API. */
  tourMode?: boolean;
  initialEvents?: CalendarEvent[];
  initialMeta?: CalendarMeta;
  /** Product tour: open month view on fixture “now” instead of today. */
  initialDate?: string | Date;
};

export function CalendarPage(props: CalendarPageProps) {
  const title = props.fallbackTitle ?? 'Calendar';
  return (
    <Suspense fallback={<DiscoverySuspenseFallback title={title} description={props.description} />}>
      <CalendarPageContent {...props} />
    </Suspense>
  );
}

function CalendarPageContent({
  fallbackTitle = 'Calendar',
  description,
  tourMode = false,
  initialEvents,
  initialMeta,
  initialDate,
}: CalendarPageProps) {
  const { isAuthenticated } = useAuth();
  const searchParams = useSearchParams();
  const linkedEventId = searchParams.get('event');
  const [meta, setMeta] = useState<CalendarMeta | null>(
    tourMode
      ? (initialMeta ?? {
          title: fallbackTitle,
          canEdit: false,
          canSubmitEvent: false,
          timeZone: 'America/Denver',
          sidebarScope: 'discovery',
        })
      : null,
  );
  const [events, setEvents] = useState<CalendarEvent[]>(tourMode ? (initialEvents ?? []) : []);
  const [myRequests, setMyRequests] = useState<CalendarEventRequest[]>([]);
  const [view, setView] = useState<ViewName>('dayGridMonth');
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [loading, setLoading] = useState(!tourMode);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [requestMode, setRequestMode] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [initialDraft, setInitialDraft] = useState<ReturnType<typeof draftFromEvent> | undefined>();
  const [calendarTitle, setCalendarTitle] = useState('');
  const deepLinkOpened = useRef<string | null>(null);
  const Page = tourMode ? SitePageLayout : SiteShell;

  const loadMeta = useCallback(() => {
    if (tourMode) return;
    fetchCalendarMeta()
      .then((m) => {
        setMeta(m);
        if (m.canSubmitEvent && isAuthenticated) {
          fetchMyCalendarEventRequests()
            .then((res) => setMyRequests(res.requests ?? []))
            .catch(() => setMyRequests([]));
        } else {
          setMyRequests([]);
        }
      })
      .catch((err: Error) => setError(err.message));
  }, [isAuthenticated, tourMode]);

  const loadEvents = useCallback(
    (from: string, to: string) => {
      if (tourMode) {
        setEvents(initialEvents ?? []);
        setLoading(false);
        return;
      }
      setLoading(true);
      fetchCalendarEvents(from, to)
        .then((res) => setEvents(res.events ?? []))
        .catch((err: Error) => setError(err.message))
        .finally(() => setLoading(false));
    },
    [tourMode, initialEvents],
  );

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (range) loadEvents(range.from, range.to);
  }, [range, loadEvents]);

  useEffect(() => {
    if (tourMode) return;
    if (!linkedEventId) {
      deepLinkOpened.current = null;
      return;
    }
    if (deepLinkOpened.current === linkedEventId) return;

    const openLinked = (event: CalendarEvent) => {
      deepLinkOpened.current = linkedEventId;
      setSelectedEvent(event);
      setRequestMode(false);
      setInitialDraft(undefined);
      setModalOpen(true);
    };

    const inView = events.find((e) => e.id === linkedEventId);
    if (inView) {
      openLinked(inView);
      return;
    }
    if (!range) return;

    let cancelled = false;
    const { from, to } = upcomingEventsRange();
    fetchCalendarEvents(from, to)
      .then((res) => {
        if (cancelled || deepLinkOpened.current === linkedEventId) return;
        const found = (res.events ?? []).find((e) => e.id === linkedEventId);
        if (found) openLinked(found);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [linkedEventId, events, range, tourMode]);

  const fcEvents = useMemo(() => events.map(apiEventToFullCalendar), [events]);

  function onDatesSet(arg: DatesSetArg) {
    setCalendarTitle(arg.view.title);
    setRange({ from: arg.start.toISOString(), to: arg.end.toISOString() });
  }

  function openCreate(select?: DateSelectArg) {
    if (!meta?.canEdit) return;
    setRequestMode(false);
    setSelectedEvent(null);
    if (select) {
      const start = select.start;
      const end = select.end;
      const allDay = select.allDay;
      setInitialDraft({
        ...draftFromEvent(null),
        startDate: toDateInput(start),
        endDate: toDateInput(allDay ? new Date(end.getTime() - 86400000) : end),
        startTime: toTimeInput(start),
        endTime: toTimeInput(end),
        allDay,
      });
    } else {
      setInitialDraft(undefined);
    }
    setModalOpen(true);
  }

  function openSuggest() {
    if (!meta?.canSubmitEvent) return;
    setRequestMode(true);
    setSelectedEvent(null);
    setInitialDraft(undefined);
    setModalOpen(true);
  }

  function openEventClick(arg: EventClickArg) {
    const raw = arg.event.extendedProps.raw as CalendarEvent | undefined;
    if (!raw) return;
    setSelectedEvent(raw);
    setRequestMode(false);
    setInitialDraft(undefined);
    setModalOpen(true);
  }

  function refresh() {
    loadMeta();
    if (range) loadEvents(range.from, range.to);
  }

  const pendingCount = myRequests.filter((r) => r.status === 'pending').length;
  const rejectedRequest = myRequests.find((r) => r.status === 'rejected');

  const headerActions = (
    <>
      {meta?.canEdit && (
        <button type="button" className="btn btn-primary" onClick={() => openCreate()}>
          + Create
        </button>
      )}
      {meta?.canSubmitEvent && (
        <button
          type="button"
          className={meta?.canEdit ? 'btn btn-secondary' : 'btn btn-primary'}
          onClick={openSuggest}
        >
          Suggest event
        </button>
      )}
    </>
  );

  const pageAlerts = (
    <>
      {pendingCount > 0 && (
        <p className="text-muted calendar-page__status">
          You have {pendingCount} event submission{pendingCount === 1 ? '' : 's'} pending review.
        </p>
      )}
      {rejectedRequest && (
        <p className="text-muted calendar-page__status">
          Your event &ldquo;{rejectedRequest.title}&rdquo; was not approved
          {rejectedRequest.rejectReason ? `: ${rejectedRequest.rejectReason}` : ''}.
        </p>
      )}
      {error && <p className="form-error">{error}</p>}
      {loading && events.length === 0 && <p className="text-muted">Loading events…</p>}
    </>
  );

  return (
    <Page
      title={meta?.title ?? fallbackTitle}
      description={description ?? 'Community events and important dates.'}
      actions={headerActions}
      alerts={pageAlerts}
    >
      <div className="calendar-page">
        <div className="calendar-toolbar">
          <div className="calendar-toolbar__left">
            <button type="button" className="btn btn-secondary calendar-toolbar__today" onClick={() => {
              document.querySelector<HTMLElement>('.calendar-page .fc-today-button')?.click();
            }}>
              Today
            </button>
            <div className="calendar-toolbar__nav">
              <button type="button" className="btn btn-secondary" aria-label="Previous" onClick={() => {
                document.querySelector<HTMLElement>('.calendar-page .fc-prev-button')?.click();
              }}>
                ‹
              </button>
              <button type="button" className="btn btn-secondary" aria-label="Next" onClick={() => {
                document.querySelector<HTMLElement>('.calendar-page .fc-next-button')?.click();
              }}>
                ›
              </button>
            </div>
            <h3 className="calendar-toolbar__title">{calendarTitle}</h3>
          </div>
          <div className="calendar-toolbar__views">
            {VIEW_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`calendar-toolbar__view${view === tab.id ? ' calendar-toolbar__view--active' : ''}`}
                onClick={() => setView(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView={view}
          initialDate={initialDate}
          key={view}
          headerToolbar={false}
          events={fcEvents}
          height="auto"
          selectable={meta?.canEdit ?? false}
          selectMirror
          nowIndicator
          datesSet={onDatesSet}
          select={openCreate}
          eventClick={openEventClick}
          dayMaxEvents
        />

        {meta?.canEdit && <CalendarEventRequestsPanel onChanged={refresh} />}
      </div>

      <CalendarEventModal
        open={modalOpen}
        event={selectedEvent}
        canEdit={meta?.canEdit ?? false}
        requestMode={requestMode}
        isAuthenticated={isAuthenticated}
        initialDraft={initialDraft}
        onClose={() => setModalOpen(false)}
        onSaved={refresh}
      />
    </Page>
  );
}
